/**
 * Exchange form arithmetic (Exchange screen spec, sections 3.3–3.6).
 *
 * Amounts, price, fee and total are decimal strings. Fee and total are sent
 * to the server and compared exactly, so they are computed with integer
 * arithmetic (BigInt) instead of floats — 498 × 0.005 must be "2.49", not
 * 2.4899999999999998 rounded the wrong way.
 */

// ─── Decimal strings ─────────────────────────────────────────────────────

interface Scaled {
  units: bigint;
  scale: number;
}

const parseDecimal = (raw: string): Scaled | null => {
  const s = raw.trim();
  if (!/^\d+(\.\d+)?$/.test(s)) { return null; }
  const [whole, fraction = ''] = s.split('.');
  return { units: BigInt(whole + fraction), scale: fraction.length };
};

const pow10 = (n: number): bigint => BigInt(10) ** BigInt(n);

const rescale = (d: Scaled, scale: number): bigint =>
  scale >= d.scale ? d.units * pow10(scale - d.scale) : d.units / pow10(d.scale - scale);

const format = ({ units, scale }: Scaled): string => {
  if (scale === 0) { return units.toString(); }
  const digits = units.toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, -scale);
  const fraction = digits.slice(-scale).replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
};

/** a × b, exact. Invalid input gives "0". */
export const multiplyDecimal = (a: string, b: string): string => {
  const x = parseDecimal(a);
  const y = parseDecimal(b);
  if (!x || !y) { return '0'; }
  return format({ units: x.units * y.units, scale: x.scale + y.scale });
};

/** a + b, exact. Invalid input counts as 0. */
export const addDecimal = (a: string, b: string): string => {
  const x = parseDecimal(a) ?? { units: BigInt(0), scale: 0 };
  const y = parseDecimal(b) ?? { units: BigInt(0), scale: 0 };
  const scale = Math.max(x.scale, y.scale);
  return format({ units: rescale(x, scale) + rescale(y, scale), scale });
};

/** a − b, exact (may be negative). Invalid input counts as 0. */
export const subtractDecimal = (a: string, b: string): string => {
  const x = parseDecimal(a) ?? { units: BigInt(0), scale: 0 };
  const y = parseDecimal(b) ?? { units: BigInt(0), scale: 0 };
  const scale = Math.max(x.scale, y.scale);
  const units = rescale(x, scale) - rescale(y, scale);
  return units < BigInt(0) ? `-${format({ units: -units, scale })}` : format({ units, scale });
};

/** Rounds up (towards +∞) to `places` decimals. */
export const ceilDecimal = (raw: string, places: number): string => {
  const d = parseDecimal(raw);
  if (!d) { return '0'; }
  if (d.scale <= places) { return format(d); }
  const divisor = pow10(d.scale - places);
  const truncated = d.units / divisor;
  const rounded = d.units % divisor === BigInt(0) ? truncated : truncated + BigInt(1);
  return format({ units: rounded, scale: places });
};

// ─── Number rounding for derived amounts ─────────────────────────────────

// Floats carry binary noise (498 / 332 * 332 ≠ 498 exactly); snapping to 9
// significant fractional digits first keeps floor/ceil from stepping over a
// value that is really exact.
const snap = (value: number, factor: number) => Number((value * factor).toFixed(6));

/** Rounds down to `decimals`, trailing zeros trimmed; "" when not positive. */
export const floorAmount = (value: number, decimals: number): string => {
  if (!Number.isFinite(value) || value <= 0) { return ''; }
  const factor = Math.pow(10, decimals);
  return (Math.floor(snap(value, factor)) / factor).toFixed(decimals).replace(/\.?0+$/, '');
};

/** Rounds up to `decimals`, trailing zeros trimmed; "" when not positive. */
export const ceilAmount = (value: number, decimals: number): string => {
  if (!Number.isFinite(value) || value <= 0) { return ''; }
  const factor = Math.pow(10, decimals);
  return (Math.ceil(snap(value, factor)) / factor).toFixed(decimals).replace(/\.?0+$/, '');
};

// ─── Pairs (3.6) ─────────────────────────────────────────────────────────

export const COUNTER_SYMBOL = 'USDT';

const USD_PEGGED = new Set(['USDT', 'USDC', 'USD']);

export const isUsdPeggedSymbol = (symbol: string): boolean => USD_PEGGED.has(symbol.toUpperCase());

const isCounter = (symbol: string) => symbol.toUpperCase() === COUNTER_SYMBOL;

/**
 * Whether `candidate` may stand opposite `other`: every trade has USDT on
 * one side of it.
 *
 * Crypto used to be swappable straight into crypto. That left pairs with
 * no USDT anywhere — BTC for ETH — and the fee had nowhere honest to go:
 * charging it in USDT would mean reaching into a third balance the client
 * may not have, and charging it in BTC made "the fee is USDT" untrue.
 * Routing every trade through USDT is the rule that makes the fee one
 * currency without inventing a debit.
 *
 * With nothing chosen on the other side everything is allowed.
 */
export const isCompatiblePair = (
  candidate: string,
  other: string | null | undefined,
  isStock: (symbol: string) => boolean,
): boolean => {
  if (!other) { return true; }
  if (isCounter(candidate) === isCounter(other)) { return false; }
  // Exactly one side is USDT, which also rules out stock-for-stock.
  return true;
};

// ─── Price and amounts (3.3–3.5) ─────────────────────────────────────────

/**
 * crypto    — price is 1 Buy token in Sell tokens:   Sell = Buy × Price
 * buyStock  — price is 1 share in USDT (Sell side):  Sell = Buy × Price
 * sellStock — price is still 1 share in USDT:        Buy  = Sell × Price
 */
export type PairKind = 'crypto' | 'buyStock' | 'sellStock';

export const pairKind = (sellIsStock: boolean, buyIsStock: boolean): PairKind =>
  sellIsStock ? 'sellStock' : buyIsStock ? 'buyStock' : 'crypto';

export interface AmountContext {
  kind: PairKind;
  sellDecimals: number;
  buyDecimals: number;
  sellSymbol: string;
  /**
   * Fee as a fraction ("0.02"). The fee sits inside the sold amount, so it
   * is withheld before the conversion. Absent means "not loaded yet" and
   * the amounts convert one to one, as they did before the fee moved in.
   */
  feeRate?: string;
  /**
   * The price is quoted the other way round: 1 Sell token in Buy tokens
   * (1 BTC = 84 000 USDT), not the server's 1 Buy token in Sell tokens. Buy
   * is then Sell × price, not Sell ÷ price. See priceQuotedInverted.
   */
  priceInverted?: boolean;
}

/**
 * Sell amount derived from another field, rounded up (3.4): USD-pegged
 * amounts to the cent, other tokens — including shares — to their
 * App_Decimals from the Currencies reference.
 */
const deriveSell = (value: number, ctx: AmountContext): string => {
  if (ctx.kind !== 'sellStock' && isUsdPeggedSymbol(ctx.sellSymbol)) {
    return ceilAmount(value, Math.min(2, ctx.sellDecimals));
  }
  return ceilAmount(value, ctx.sellDecimals);
};

/** Buy amount derived from another field: always down to App_Decimals (3.4). */
const deriveBuy = (value: number, ctx: AmountContext): string => floorAmount(value, ctx.buyDecimals);

/** The rate as a number; 0 when it is unknown or nonsense. */
const rateOf = (ctx: AmountContext): number => {
  const rate = parseFloat(ctx.feeRate ?? '');
  return Number.isFinite(rate) && rate > 0 && rate < 1 ? rate : 0;
};

/**
 * Whether the fee comes out of the proceeds rather than out of the amount
 * sold. The fee is always USDT, so it lands on whichever side USDT is:
 * selling USDT it is inside the amount, selling anything else it is
 * withheld from what the client receives.
 */
export const feeChargedOnProceeds = (sellSymbol: string): boolean => !isCounter(sellSymbol);

const feeOnBuySide = (ctx: AmountContext): boolean => feeChargedOnProceeds(ctx.sellSymbol);

/**
 * The token the fee is charged in. Selling anything but USDT it is the
 * bought token, which the pair rule makes USDT — so before Buy is picked
 * the answer is already known, and the row can say "0 USDT" instead of a
 * bare "0" with no currency against it.
 */
export const feeSymbolFor = (sellSymbol: string, buySymbol: string): string =>
  feeChargedOnProceeds(sellSymbol) ? (buySymbol || COUNTER_SYMBOL) : sellSymbol;

/**
 * Fee withheld from a sold amount, rounded up the same way the order's fee
 * is (to the cent for USD-pegged tokens, else App_Decimals capped at 8).
 * Zero when the fee is on the buy side — a stock sale, or any sale for
 * USDT — because there it comes out of the proceeds instead.
 */
const feeOnSell = (sell: number, ctx: AmountContext): number => {
  const rate = rateOf(ctx);
  if (rate === 0 || feeOnBuySide(ctx)) { return 0; }
  const decimals = isUsdPeggedSymbol(ctx.sellSymbol)
    ? Math.min(2, ctx.sellDecimals)
    : Math.min(Math.max(ctx.sellDecimals, 0), 8);
  return parseFloat(ceilAmount(sell * rate, decimals)) || 0;
};

/** User changed Sell: Buy follows, priced on what is left after the fee. */
export const buyFromSell = (sell: number, price: number, ctx: AmountContext): string => {
  if (!(sell > 0 && price > 0)) { return ''; }
  // A stock is priced in USDT per share whichever way it is going, so
  // selling shares multiplies where everything else divides.
  if (ctx.kind === 'sellStock') { return deriveBuy(sell * price, ctx); }
  const net = sell - feeOnSell(sell, ctx);
  if (net <= 0) { return ''; }
  return deriveBuy(ctx.priceInverted ? net * price : net / price, ctx);
};

/**
 * User changed Buy, or the price with Buy fixed: Sell follows. It has to
 * cover both the conversion and the fee taken out of it, so the gross is
 * grossed up by the rate and rounded up — the client may end up paying a
 * fraction more than the exact figure, never less.
 */
export const sellFromBuy = (buy: number, price: number, ctx: AmountContext): string => {
  if (!(buy > 0 && price > 0)) { return ''; }
  if (ctx.kind === 'sellStock') { return deriveSell(buy / price, ctx); }
  const net = ctx.priceInverted ? buy / price : buy * price;
  // Grossing up only applies when the fee is inside the amount sold.
  const rate = feeOnBuySide(ctx) ? 0 : rateOf(ctx);
  return deriveSell(rate === 0 ? net : net / (1 - rate), ctx);
};

/** Fee rounded up: to the cent for USD-pegged tokens, else to App_Decimals (max 8). */
const roundFee = (exact: string, symbol: string, decimals: number): string =>
  isUsdPeggedSymbol(symbol) ? ceilDecimal(exact, 2) : ceilDecimal(exact, Math.min(Math.max(decimals, 0), 8));

export interface ExchangeFees {
  fee: string;
  /** Token the fee is charged in. */
  feeSymbol: string;
  /** Total Balance — what gets locked on the sell account. */
  total: string;
  /** What the client receives in the buy token. */
  receive: string;
}

/**
 * Fee, Total Balance and proceeds (3.4). The fee is charged in the sell token
 * on top of the amount (Total = Sell + Fee) — except when selling a stock:
 * then it is charged in USDT from the proceeds, only the shares are locked
 * (Total = Sell) and the client receives Buy − Fee. Mirrors the server,
 * which refuses an order whose figures differ.
 */
export const exchangeFees = (params: {
  kind: PairKind;
  sell: string;
  buy: string;
  feeRate: string;
  sellSymbol: string;
  buySymbol: string;
  sellDecimals: number;
  buyDecimals: number;
}): ExchangeFees => {
  const { sell, buy, feeRate, sellSymbol, buySymbol, sellDecimals, buyDecimals } = params;
  // The fee is USDT, whatever is being traded — and since every pair has
  // USDT on one side, it is one of the two tokens in front of the client.
  //
  // Selling USDT, it comes out of the amount sold: the whole amount is
  // locked and the client receives what the rest converts into. Selling
  // anything else, USDT is what they are buying, and the fee is withheld
  // from the proceeds. A stock sale has always worked the second way;
  // selling BTC for USDT used to charge in BTC and now does the same.
  if (feeChargedOnProceeds(sellSymbol)) {
    const feeSymbol = feeSymbolFor(sellSymbol, buySymbol);
    const fee = roundFee(multiplyDecimal(buy || '0', feeRate), feeSymbol, buyDecimals);
    return { fee, feeSymbol, total: sell || '0', receive: subtractDecimal(buy || '0', fee) };
  }
  const fee = roundFee(multiplyDecimal(sell || '0', feeRate), sellSymbol, sellDecimals);
  return { fee, feeSymbol: sellSymbol, total: sell || '0', receive: buy || '0' };
};

/** Label for the price rows: the stock for stock pairs, else the Buy token. */
export const priceUnitSymbol = (kind: PairKind, sellSymbol: string, buySymbol: string): string =>
  kind === 'sellStock' ? sellSymbol : buySymbol;

// ─── Price direction ─────────────────────────────────────────────────────

/** Places the server stores a price with: conversion_rate is NUMERIC(36, 18). */
export const PRICE_SERVER_PLACES = 18;

/**
 * Whether the form quotes the price the other way round from the server.
 * The server prices 1 Buy token in Sell tokens; selling BTC for USDT that is
 * 0,0000119 BTC — three significant digits at eight places, so a limit at
 * 84 000 could not be set and a market order's amounts drifted from it.
 * Selling a token for USDT, the form quotes 1 Sell token in USDT instead, so
 * every crypto price reads in USDT whichever way the order goes. Decided by
 * the pair, not by the rate, so a rate near 1 cannot flip it mid-entry.
 */
export const priceQuotedInverted = (kind: PairKind, sellSymbol: string, buySymbol: string): boolean =>
  kind === 'crypto' && isCounter(buySymbol) && !isCounter(sellSymbol);

/** 1 ÷ value as a decimal string at `places`, rounded half up; invalid or zero → "". */
export const invertDecimal = (raw: string, places: number = PRICE_SERVER_PLACES): string => {
  const d = parseDecimal(raw.replace(',', '.'));
  if (!d || d.units === BigInt(0)) { return ''; }
  // 1 / (units / 10^scale) = 10^scale / units, at `places`: × 10^places.
  const numerator = pow10(d.scale + places) * BigInt(2);
  const units = (numerator / d.units + BigInt(1)) / BigInt(2);
  return format({ units, scale: places });
};

/** A float rate as a plain decimal string at up to `places`, no exponent, no trailing zeros. */
export const rateToText = (value: number, places: number = PRICE_SERVER_PLACES): string => {
  if (!Number.isFinite(value) || value <= 0) { return ''; }
  // Fifteen significant digits are what a double carries reliably; past
  // them toFixed adds noise (0.1 + 0.2 → 0.30000000000000004), so cut there,
  // then drop the zeros.
  const magnitude = Math.floor(Math.log10(value));
  const digits = Math.max(0, Math.min(places, 14 - magnitude));
  return value.toFixed(digits).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};
